import React from 'react'

import { Box, Flex, Text } from '@chakra-ui/react'

import { SummaryPart } from '@pkg/types/interview'

import Card from '@/views/components/Card'

import { DecisionSelector } from './DecisionSelector'

type ScoresSummaryBlockProps = {
  summaryParts: SummaryPart[]
}

const getScoreColor = (score: number): string => {
  const colorMap: Record<number, string> = {
    1: '#C00',
    2: '#F60',
    3: '#FC0',
    4: '#9C0',
    5: '#090',
  }
  return colorMap[score] || '#d9d9d9'
}

export const ScoresSummaryBlock: React.FC<ScoresSummaryBlockProps> = ({
  summaryParts,
}) => {
  const scoredParts = summaryParts.filter(
    (part) =>
      part.score !== null &&
      part.score !== undefined &&
      part.CustomSummaryPartPrompt?.hasScoring &&
      part.isSelected,
  )

  if (scoredParts.length === 0) {
    return null
  }

  const totalScore = scoredParts.reduce(
    (sum, part) => sum + (part.score || 0),
    0,
  )
  const maxPossibleScore = scoredParts.length * 5
  const overallFillPercentage = (totalScore / maxPossibleScore) * 100

  const averageScore = totalScore / scoredParts.length
  const overallScoreColor = getScoreColor(Math.round(averageScore))

  return (
    <Card className="mb-5 pt-[1.25rem] pb-[1.75rem] rounded-[2.5rem] bg-[#F5F5F5] px-5 sm:px-[2.5rem]">
      <Flex
        justifyContent={'space-between'}
        alignItems={{ base: 'stretch', lg: 'center' }}
        mb={'35px'}
        gap={{ base: 4, lg: 6 }}
        flexWrap={'wrap'}
      >
        <Flex
          alignItems={'center'}
          gap={{ base: 3, sm: 4 }}
          flexWrap={'wrap'}
          minW={0}
          flex={1}
        >
          <Flex
            alignItems={'baseline'}
            gap={0}
            minW={0}
            flexWrap={'wrap'}
            flexShrink={1}
          >
            <Text fontSize={'20px'} fontWeight={'bold'} lineHeight={1}>
              Overall Score{' '}
            </Text>
            <Box display={'flex'} alignItems={'center'} gap={0}>
              <Text
                fontSize={'20px'}
                fontWeight={'bold'}
                lineHeight={1}
                ml={'8px'}
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {totalScore}
              </Text>
              <Text
                fontSize={'20px'}
                fontWeight={'bold'}
                lineHeight={1}
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                /
              </Text>
              <Text
                fontSize={'20px'}
                fontWeight={'bold'}
                lineHeight={1}
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {maxPossibleScore}
              </Text>
            </Box>
          </Flex>
          <Box
            position={'relative'}
            width={{ base: '88px', sm: '100px' }}
            height={'10px'}
            flexShrink={0}
          >
            <Box
              width="100%"
              height={'10px'}
              borderRadius={'40px'}
              backgroundColor={'#d9d9d9'}
              overflow={'hidden'}
            >
              <Box
                width={`${overallFillPercentage}%`}
                height={'100%'}
                backgroundColor={overallScoreColor}
                transition={'all 0.15s ease-in-out'}
              />
            </Box>
          </Box>
        </Flex>

        <Flex
          alignItems={'center'}
          gap={'8px'}
          flexWrap={'wrap'}
          minW={0}
          w={{ base: '100%', lg: 'auto' }}
          justifyContent={{ base: 'space-between', lg: 'flex-end' }}
        >
          <Text fontSize={'16px'} fontWeight={'500'}>
            Recommendation:
          </Text>
          <DecisionSelector
            defaultBackgroundColor="#ffffff"
            useFixedPositioning={true}
          />
        </Flex>
      </Flex>

      <Flex flexWrap={'wrap'} gap={{ base: 4, md: 6 }}>
        {scoredParts.map((part) => {
          if (!part.score) return null

          const fillPercentage = (part.score / 5) * 100
          const scoreColor = getScoreColor(part.score)

          return (
            <Box key={part.id} minW={{ base: '100%', sm: '220px' }}>
              <Flex
                alignItems={'flex-start'}
                gap={'10px'}
                mb={'15px'}
                fontWeight={'500'}
              >
                <Text
                  fontSize={'15px'}
                  lineHeight={1.2}
                  minW={0}
                  overflowWrap="anywhere"
                >
                  {part.name}
                </Text>
                <Flex alignItems={'center'} gap={0} flexShrink={0}>
                  <Box
                    width={'15px'}
                    textAlign={'right'}
                    fontSize={'15px'}
                    lineHeight={1}
                  >
                    {part.score}
                  </Box>
                  <Box
                    width={'10px'}
                    textAlign={'center'}
                    fontSize={'15px'}
                    lineHeight={1}
                  >
                    /
                  </Box>
                  <Box
                    width={'15px'}
                    textAlign={'left'}
                    fontSize={'15px'}
                    lineHeight={1}
                  >
                    5
                  </Box>
                </Flex>
              </Flex>
              <Box
                position={'relative'}
                width={{ base: '100%', sm: '140px' }}
                maxW="100%"
                height={'10px'}
              >
                <Box
                  width="100%"
                  height={'10px'}
                  borderRadius={'40px'}
                  backgroundColor={'#d9d9d9'}
                  overflow={'hidden'}
                >
                  <Box
                    width={`${fillPercentage}%`}
                    height={'100%'}
                    backgroundColor={scoreColor}
                    transition={'all 0.15s ease-in-out'}
                  />
                </Box>

                {[20, 40, 60, 80].map((position) => (
                  <Box
                    key={position}
                    position={'absolute'}
                    left={`calc(${position}% - 1px)`}
                    top={'1px'}
                    width={'2px'}
                    height={'8px'}
                    rounded={'full'}
                    backgroundColor={'rgba(255, 255, 255, 0.5)'}
                    zIndex={1}
                  />
                ))}
              </Box>
            </Box>
          )
        })}
      </Flex>
    </Card>
  )
}
